import React, { useCallback, useEffect, useState } from 'react'
import useParams from 'core/hooks/useParams'
import ModalForm from 'core/elements/modal/ModalForm'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import {
  listLeasePolicies,
  createLeasePolicy,
  updateLeasePolicy,
  deleteLeasePolicy,
} from 'openstack/components/vms/leases/actions'
import { leasePoliciesSelector } from 'openstack/components/vms/leases/selectors'
import useUpdateAction from 'core/hooks/useUpdateAction'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import Text from 'core/elements/Text'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import RadioFields from 'core/components/validatedForm/radio-fields'
import { minValueValidator } from 'core/utils/fieldValidators'
import Info from 'core/components/validatedForm/Info'

const useStyles = makeStyles((theme: Theme) => ({
  durationFields: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: 8,
  },
}))

const options = [
  { label: 'Delete VM', value: 'delete' },
  { label: 'Power Off VM', value: 'power off' },
]

export default function EditLeasePolicyModal({ rows: [tenant], onClose }) {
  const classes = useStyles()
  const [loaded, setLoaded] = useState(false)
  const [policyExists, setPolicyExists] = useState(false)
  const defaultParams = {
    enableLeasePolicy: false,
    action: 'power off',
    days: 3,
    hours: 0,
    minutes: 0,
  }

  const { params, getParamsUpdater, updateParams, setParams } = useParams<{
    days?: number
    hours?: number
    minutes?: number
    enableLeasePolicy?: boolean
    action?: string
  }>(defaultParams)

  const { loading: loadingLeasePolicies } = useListAction(listLeasePolicies, {
    initialLoadingState: true,
  })
  const leasePolicies = useSelector(leasePoliciesSelector)

  const {
    update: createLease,
    updating: creatingLease,
    error: errorCreatingLease,
    reset: resetCreateLease,
  } = useUpdateAction(createLeasePolicy)

  const {
    update: updateLease,
    updating: updatingLease,
    error: errorUpdatingLease,
    reset: resetUpdateLease,
  } = useUpdateAction(updateLeasePolicy)

  const {
    update: deleteLease,
    updating: deletingLease,
    error: errorDeletingLease,
    reset: resetDeleteLease,
  } = useUpdateAction(deleteLeasePolicy)

  useEffect(() => {
    // Prevent resetting of form from background reloads
    if (loaded || loadingLeasePolicies) {
      return
    }
    const leasePolicy = leasePolicies.find((policy) => policy.tenant_uuid === tenant.id)
    if (!leasePolicy) {
      setLoaded(true)
      return
    }
    const leaseDays = Math.floor(leasePolicy.expiry_mins / 1440)
    const leaseHours = Math.floor((leasePolicy.expiry_mins - leaseDays * 1440) / 60)
    const leaseMinutes = leasePolicy.expiry_mins - leaseDays * 1440 - leaseHours * 60

    updateParams({
      enableLeasePolicy: !!leasePolicy,
      action: leasePolicy?.action,
      days: leaseDays,
      hours: leaseHours,
      minutes: leaseMinutes,
    })
    setPolicyExists(true)
    setLoaded(true)
  }, [tenant, loadingLeasePolicies, leasePolicies, loaded])

  const handleClose = () => {
    setParams(defaultParams)
    resetCreateLease()
    resetUpdateLease()
    resetDeleteLease()
    onClose()
  }

  const submitForm = useCallback(async () => {
    const expiryMinutes = params.days * 1440 + params.hours * 60 + params.minutes
    const body = {
      vm_lease_policy: {
        tenant_uuid: tenant.id,
        expiry_mins: expiryMinutes,
        action: params.action,
      },
    }
    const updateFn = policyExists ? updateLease : createLease
    const { success } = await updateFn({
      tenantId: tenant.id,
      body,
    })
    if (success) {
      if (policyExists && !params.enableLeasePolicy) {
        deleteLease({ tenant_uuid: tenant.id })
      }
      handleClose()
    }
  }, [tenant, params, handleClose, policyExists])

  return (
    <ModalForm
      open
      title={`Edit Lease Policy`}
      onSubmit={submitForm}
      disableSubmit={!policyExists && !params.enableLeasePolicy}
      onClose={handleClose}
      submitting={creatingLease || updatingLease || deletingLease}
      error={errorCreatingLease || errorUpdatingLease || errorDeletingLease}
      submitTitle={policyExists ? `Update Lease Policy` : `Create Lease Policy`}
      loading={!loaded}
    >
      <FormFieldSection title="Lease Policy Configuration">
        <Info error>
          <Text variant="body2">
            <b>NOTE:</b> Each VM’s lease starts at its creation time and ends after the duration set
            here. If an existing VM has already surpassed that lease duration when you save these
            settings, the selected lease-end action will be executed immediately.{' '}
            <b>
              Choose Delete VM only if you are certain the tenant can safely lose any VMs that are
              already past their lease.
            </b>
          </Text>
        </Info>
        <ToggleSwitchField
          id="enableLeasePolicy"
          value={params.enableLeasePolicy}
          onChange={getParamsUpdater('enableLeasePolicy')}
          label="Enable Lease Policy"
        />
        <RadioFields
          id="action"
          title="Action Upon Lease Expiration"
          options={options}
          value={params.action}
          onChange={getParamsUpdater('action')}
          disabled={!params.enableLeasePolicy}
        />
        <Text variant="caption1">Lease Duration</Text>
        <div className={classes.durationFields}>
          <TextField
            id="days"
            label="Days"
            onChange={(value) => updateParams({ days: value })}
            value={params.days}
            type="number"
            min={0}
            validations={[minValueValidator(0)]}
            disabled={!params.enableLeasePolicy}
            required
          />
          <TextField
            id="hours"
            label="Hours"
            onChange={(value) => updateParams({ hours: value })}
            value={params.hours}
            type="number"
            min={0}
            validations={[minValueValidator(0)]}
            disabled={!params.enableLeasePolicy}
            required
          />
          <TextField
            id="minutes"
            label="Minutes"
            onChange={(value) => updateParams({ minutes: value })}
            value={params.minutes}
            type="number"
            min={0}
            validations={[minValueValidator(0)]}
            disabled={!params.enableLeasePolicy}
            required
          />
        </div>
      </FormFieldSection>
    </ModalForm>
  )
}
